export interface ActorIdStorage {
  read(): Promise<string>;
  write(value: string): Promise<void>;
  create(): string;
}

export async function resolveActorId(storage: ActorIdStorage): Promise<string> {
  const current = await storage.read();
  if (current) return current;
  const value = storage.create().replaceAll("-", "");
  await storage.write(value);
  return value;
}

export interface BrowserRuntimeCapabilities {
  document: Document;
  window: Window;
  navigator: Navigator;
  storage: Storage;
  notify(message: string): void;
}

export function createBrowserRuntime(capabilities: BrowserRuntimeCapabilities) {
  function registerServiceWorker(): void {
    if ("serviceWorker" in capabilities.navigator) {
      void capabilities.navigator.serviceWorker.register("/sw.js");
    }
  }

  async function requestPersistentStorage(): Promise<void> {
    if (!capabilities.navigator.storage?.persist) return;
    const persistent = await capabilities.navigator.storage.persisted().catch(() => false);
    if (!persistent) await capabilities.navigator.storage.persist().catch(() => false);
  }

  function motionBehavior(): ScrollBehavior {
    return capabilities.window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
  }

  function updateBackToTopVisibility(): void {
    capabilities.document.querySelector<HTMLElement>(".back-to-top")?.classList.toggle("is-visible", capabilities.window.scrollY > 560);
  }

  function cycleTheme(): void {
    const current = capabilities.storage.getItem("theme") || "system";
    const next = current === "system" ? "light" : current === "light" ? "dark" : "system";
    capabilities.storage.setItem("theme", next);
    capabilities.document.documentElement.dataset.theme = next;
    capabilities.notify(`Theme: ${next}`);
  }

  return { registerServiceWorker, requestPersistentStorage, motionBehavior, updateBackToTopVisibility, cycleTheme };
}
